// The ⛓ on an initiative row: link the combatant's Roll20 token to whatever
// shares its square (a mount, aura art, a marker) so they move as one while
// staying separate tokens. The picker asks the connector's token lane for the
// overlap, the DM names the base and ticks the riders; Roll20's own grouping
// is an opt-in because it makes the members a single selection.
// TokenFollowKeeper (mounted once) re-pushes the campaign's table whenever
// the lane comes up and every 45 s, so a Roll20 reload does not lose it.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useFocusTrap } from "./FocusTrap";
import { ATTACHMENT_KINDS, DEFAULT_KIND, kindIsSticky } from "../state/attachments";
import { useStore } from "../state/store";
import { aboard, applyLaneEvents, currentTurnName, dropLink, dropLinks, guessBase, isEffectToken, keepKinds, laneEventLine, laneEventsToAnnounce, linkCandidates, mergeLink, proneMarkerOf, sameToken, subscribeTokenLane, tokenFollowOn, tokenLaneReady, tokenLinksOf, tokenOp, withTokenLinks, type LaneToken, type TokenLink, type TokenRef } from "../state/tokenLinks";
import { cellOf, readMap } from "../state/auraReach";
import { movesOf, overlaps, pairKey, roleOf, seatMoves, TOKEN_ROLES, withMove, withRole, type MoveAnswer, type MoveRecord, type RiderToken, type TokenRole } from "../state/riders";
import { chatAction, linkStyleOf, mintMoveLink } from "../state/turnLinks";
import { newRequestId, onBridge, request, useBridgeStatus, useHealth } from "../bridge";

const sq = (n: number) => (Math.round((n / 70) * 10) / 10).toString().replace(/\.0$/, "");
export const useTokenLane = () => useSyncExternalStore(subscribeTokenLane, tokenLaneReady, tokenLaneReady);


export function TokenLinkButton({ name, tokenId }: { name: string; tokenId?: string }) {
  const { layout, update, logActivity } = useStore();
  const ready = useTokenLane();
  const [open, setOpen] = useState(false);
  /* Non-modal: a popup beside its button. Focus moves in, Escape closes it, Tab
     is free to leave -- the page behind it is not unavailable. */
  const pick = useFocusTrap<HTMLSpanElement>(() => setOpen(false), open, { trap: false });
  const [busy, setBusy] = useState("");
  const [page, setPage] = useState("");
  const [tokens, setTokens] = useState<LaneToken[] | null>(null);
  const [base, setBase] = useState("");
  /* What each token is to the base, by token id. "" means not attached at all.
     The rider set and the attached set are DERIVED from it: the DM was being
     asked to say the same thing three times (tick it, then say whether it is a
     rider or attached, having already picked the base), and one dropdown says
     all of it. */
  const [kinds, setKinds] = useState<Map<string, string>>(new Map());
  const setKind = (id: string, kind: string) => setKinds((m) => { const n = new Map(m); if (kind) n.set(id, kind); else n.delete(id); return n; });
  const [centre, setCentre] = useState(true);
  const [back, setBack] = useState(true);
  const [group, setGroup] = useState(false);
  const [remount, setRemount] = useState(true);
  const [mount, setMount] = useState(true);
  /* What may be attached at all: tokens on the TOKEN layer.
     Map and GM-layer art are left out of the picker entirely — a map file name
     can give away more than the DM meant to share, and players reach these
     controls too. The same set feeds the rows AND the kinds, so nothing can
     become a rider that the DM was never shown; filtering only the rows (an
     earlier attempt at this) left the model carrying it and hid the behaviour
     rather than stopping it. Unlink still works from the FULL list, because
     clearing a stale group has to reach what the picker will not offer. */
  const candidates = useMemo(() => linkCandidates(tokens), [tokens]);
  const riders = useMemo(() => new Set([...kinds].filter(([, k]) => Boolean(k)).map(([id]) => id)), [kinds]);
  const attached = useMemo(() => new Set([...kinds].filter(([, k]) => kindIsSticky(k)).map(([id]) => id)), [kinds]);
  const [note, setNote] = useState("");
  /* The ⛓ button sits mid-row, so a panel anchored to one side runs off the
     other edge depending on how wide the window is. Measured when it opens:
     a button past halfway opens leftwards. */
  const anchor = useRef<HTMLSpanElement | null>(null);
  const [alignRight, setAlignRight] = useState(false);
  const links = tokenLinksOf(layout);
  const mine = links.find((l) => l.base.id === tokenId || l.base.name === name || l.riders.some((r) => r.id === tokenId || r.name === name));
  if (!ready) return null;

  const load = async () => {
    setOpen(true); setBusy("Reading the map…"); setNote(""); setTokens(null);
    const r = await tokenOp({ op: "overlap", ...(tokenId && tokenId !== "-1" ? { id: tokenId } : {}), name });
    setBusy("");
    if (!r.ok || !r.tokens) { setNote(r.error || "No answer from the Roll20 tab."); return; }
    setPage(r.page || "");
    setTokens(r.tokens);
    /* A stored base is honoured only while it is still a candidate: links made
       before the layer rule could name the battlemap as the base, and reopening
       the picker would adopt it all over again. */
    const pool = linkCandidates(r.tokens);
    const b = mine?.base.id && pool.some((t) => t.id === mine.base.id) ? mine.base.id : guessBase(pool);
    setBase(b);
    setRemount(mine ? mine.remount !== false : true);
    setMount(mine ? mine.mount === true : true);
    // Effects and art are attached art and nothing else; a link already made
    // keeps the kind it was given, falling back to a plain follow for links
    // recorded before kinds existed.
    setKinds(new Map(pool.filter((t) => t.id !== b).flatMap((t) => {
      /* By id OR name. The link above is found by name, so looking the rider up
         by id alone lost the kind on any token that had been replaced. */
      const was = mine?.riders.find((x) => sameToken(x, t));
      if (isEffectToken(t)) return [[t.id, "art"] as [string, string]];
      if (!was) return [];
      return [[t.id, was.kind || (was.attached ? "art" : DEFAULT_KIND)] as [string, string]];
    })));
    // Nothing is ticked that is not already following: overlapping tokens are
    // usually a coincidence (a creature standing on a rug, on a trapdoor, on
    // another creature it is only passing), and pre-ticking them all meant
    // every Link click dragged the scenery along until the DM untangled it.
    const following0 = pool;
    /* EXCLUSIVE: only what this dialog names gets linked.
       A token already grouped in Roll20 (t.linked === b) used to be seeded as a
       rider automatically, which is how the battlemap kept re-attaching itself:
       linked once by accident, it was then silently re-included in every link
       that followed, whatever the picker chose to display. Roll20's own grouping
       is now read as information, never as consent. Only our OWN stored link
       restores a kind, because that is a choice the DM made here. */
    setKinds((m) => {
      const n = new Map(m);
      for (const t of following0) {
        if (t.id === b) continue;
        const was = mine ? aboard(mine).find((x) => sameToken(x, t)) : undefined;
        if (was && !n.has(t.id)) n.set(t.id, was.kind || DEFAULT_KIND);
      }
      return n;
    });
  };
  const link = async () => {
    if (!tokens || !base) return;
    const rs = tokens.filter((t) => riders.has(t.id) && t.id !== base);
    if (!rs.length) { setNote("Tick at least one token to ride along."); return; }
    setBusy("Linking…");
    const r = await tokenOp({ op: "link", base, riders: rs.map((t) => ({ id: t.id, name: t.name, attached: attached.has(t.id), kind: kinds.get(t.id) || DEFAULT_KIND })), centre, back, group, remount, mount, proneMarker: proneMarkerOf(layout) });
    setBusy("");
    if (!r.ok) { setNote(r.error || "Roll20 refused."); return; }
    const b = tokens.find((t) => t.id === base)!;
    const entry: TokenLink = { base: { id: b.id, name: b.name }, riders: rs.map((t) => ({ id: t.id, name: t.name, attached: attached.has(t.id), kind: kinds.get(t.id) || DEFAULT_KIND })), remount, mount };
    update((l) => withTokenLinks(l, mergeLink(tokenLinksOf(l), entry)));
    const aboard = rs.filter((t) => !attached.has(t.id)), art = rs.filter((t) => attached.has(t.id));
    if (aboard.length) logActivity({ text: `${aboard.map((t) => t.name).join(", ")} ${aboard.length === 1 ? (mount ? "mounts" : "climbs onto") : (mount ? "mount" : "climb onto")} ${b.name || "the base"}`, meta: group ? "linked on the map and grouped in Roll20" : "linked on the map; each still its own token" });
    if (art.length) logActivity({ text: `${art.map((t) => t.name || "art").join(", ")} now ${art.length === 1 ? "follows" : "follow"} ${b.name || "the base"}`, meta: "attached on the map: moves with it, never dismounts" });
    setOpen(false);
  };
  const unlink = async () => {
    // the group this token is the base of, else the one it rides in; a chain
    // above or below it is left alone
    const grp = links.find((l) => l.base.id === tokenId || (l.base.name === name && !tokenId)) || mine;
    /* Unlink clears the group on EVERY token sharing the square, not just the
       base and its followers. A stale group can hold something the picker no
       longer offers — map art especially, which is filtered out of the rows so
       it can never be attached again — and if unlink only reached the rows, that
       group could never be cleared from the interface at all. Its own tooltip
       has always said "every token in the list"; this makes that true. */
    const ids = grp ? [] : tokens ? tokens.map((t) => t.id) : [];
    if (!grp && !ids.length) { setOpen(false); return; }
    setBusy("Unlinking…");
    const r = await tokenOp(grp ? { op: "unlink", base: grp.base.id } : { op: "unlink", ids });
    setBusy("");
    if (!r.ok) { setNote(r.error || "Roll20 refused."); return; }
    update((l) => withTokenLinks(l, grp ? dropLink(tokenLinksOf(l), grp.base.id) : dropLinks(tokenLinksOf(l), ids)));
    logActivity({ text: `${name}: tokens unlinked on the map` });
    setOpen(false);
  };
  const title = mine ? `Linked on the map: ${mine.riders.map((r) => (r.name || "art") + (r.off ? " (off)" : "")).join(", ")} ${mine.riders.length === 1 ? "follows" : "follow"} ${mine.base.name || "the base"}. Click to change or unlink.` : "Link this token to what shares its square (a mount, aura art, a marker): drag the base and the riders come along, each still its own token. A rider dragged clear of the base dismounts.";
  return (
    <span className="token-link" ref={anchor} onClick={(e) => e.stopPropagation()}>
      <button className={`ghost small ${mine ? "on" : ""}`} onClick={() => { if (open) return setOpen(false); const r = anchor.current?.getBoundingClientRect(); setAlignRight(Boolean(r && r.left > window.innerWidth / 2)); void load(); }} title={title} aria-pressed={Boolean(mine)} aria-label={`Link ${name}'s token`}>⛓</button>
      {open && <span ref={pick} className={`duration token-link-pick ${alignRight ? "align-right" : ""}`} role="dialog" aria-label={`Link ${name}'s token`}>
        <label title="Off: the follow is not re-applied to the Roll20 tab (links are kept for when it is on again)."><input type="checkbox" checked={tokenFollowOn(layout)} onChange={(e) => update((l) => ({ ...l, tokenFollow: e.target.checked }))} /> linked tokens active</label>
        {busy && <em>{busy}</em>}
        {note && <em className="warn">{note}</em>}
        {tokens && <>
          <small>{page ? `On ${page}: ` : ""}what shares {name}'s square on the token layer, back to front. Nothing follows until you give it a kind.{tokens && tokens.length > candidates.length ? ` ${tokens.length - candidates.length} off-layer item${tokens.length - candidates.length === 1 ? "" : "s"} not shown.` : ""}</small>
          <div className="token-link-head" aria-hidden="true">
            <span className="col-primary" title="The one token that leads: drag it and everything ticked in the next column comes along.">◉ Primary</span>
            <span className="col-follows" title="These follow the primary. A creature can be dragged clear to dismount; an attachment never leaves.">☑ Follows it</span>
            <span className="col-rest" />
          </div>
          {candidates.map((t) => <label key={t.id} className={`token-link-row ${base === t.id ? "is-primary" : riders.has(t.id) ? "is-follower" : ""}`}>
            <input type="radio" name={`tkBase-${name}`} checked={base === t.id} disabled={isEffectToken(t)} onChange={() => { setBase(t.id); setKind(t.id, ""); }} title={isEffectToken(t) ? "An effect or art cannot be the base" : "Base"} />
            {t.imgsrc ? <img src={t.imgsrc} alt="" width={18} height={18} /> : <span className="token-link-blank" />}
            <span>{t.name || "(unnamed art)"}</span>
            <small>{sq(t.width)}×{sq(t.height)} sq · {t.layer} layer{t.group ? " · Roll20 group" : ""}{t.linked ? " · linked" : ""}</small>
            {base !== t.id && <select className="xs token-link-kind" value={kinds.get(t.id) || ""} disabled={isEffectToken(t)} aria-label={`What ${t.name || "this art"} is to the base`} title={isEffectToken(t) ? "Effects and art follow and never come loose" : ATTACHMENT_KINDS.map((k) => `${k.label}: ${k.hint}`).join("\n\n")} onChange={(e) => setKind(t.id, e.target.value)}>
              <option value="">— not attached —</option>
              {ATTACHMENT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>}
          </label>)}
          <label><input type="checkbox" checked={centre} onChange={(e) => setCentre(e.target.checked)} /> centre the riders on the base</label>
          <label><input type="checkbox" checked={back} onChange={(e) => setBack(e.target.checked)} /> base behind the riders</label>
          <label title="On: a controlled mount — the log says mounts / dismounts. Off: a creature climbed onto — climbs onto / climbs off, and thrown on someone else's turn or when the base falls prone."><input type="checkbox" checked={mount} onChange={(e) => setMount(e.target.checked)} /> controlled mount (else: climbs on)</label>
          <label title="A base with a status marker named like this throws whoever is aboard. Any marker whose name holds the word counts.">prone marker <input className="xs" value={proneMarkerOf(layout)} onChange={(e) => update((l) => ({ ...l, tokenProneMarker: e.target.value }))} aria-label="Prone marker name" /></label>
          <label title="A rider the player drags clear of the base has dismounted: the link drops, and it is said in the event log and in Roll20 chat. On: dropping it back on the base re-mounts it."><input type="checkbox" checked={remount} onChange={(e) => setRemount(e.target.checked)} /> re-mount when dropped back on</label>
          <label title="Mounts, dismounts and being thrown are announced in Roll20 chat, so the table knows who is back on foot. Off: the event log only, which only you see."><input type="checkbox" checked={layout.tokenLinkEmotes !== false} onChange={(e) => update((l) => ({ ...l, tokenLinkEmotes: e.target.checked }))} /> say mounts and dismounts in chat</label>
          <label title="Roll20's own grouping makes the members one selection — bars and damage then need an ungroup first. Off, each token stays its own."><input type="checkbox" checked={group} onChange={(e) => setGroup(e.target.checked)} /> Roll20 group as well</label>
          <span className="row">
            <button className="small" onClick={() => void link()} disabled={Boolean(busy)}>Link</button>
            <button className="ghost small" onClick={() => void unlink()} disabled={Boolean(busy)}>Unlink</button>
            <button className="ghost small" onClick={() => setOpen(false)}>Close</button>
          </span>
        </>}
        {!tokens && !busy && <span className="row"><button className="ghost small" onClick={() => void load()}>Try again</button><button className="ghost small" onClick={() => setOpen(false)}>Close</button></span>}
      </span>}
    </span>
  );
}

/** Keeps the campaign's follow table alive in the game tab and listens for
 *  the players' own mounts and dismounts (a rider dragged clear of its base,
 *  or dropped back on it). Mount once. Polls every 5 s while links exist. */
export function TokenFollowKeeper() {
  const { layout, update, logActivity } = useStore();
  const ready = useTokenLane();
  const bridge = useBridgeStatus();
  const links = tokenLinksOf(layout);
  const on = tokenFollowOn(layout);
  const key = JSON.stringify(links);
  const since = useRef(0);
  /* Whether this screen has had one answer from the lane yet. The first one
     carries the lane's whole backlog (we ask from sequence 0), and replaying
     that into the game's chat on every page load would be worse than the
     silence it replaces. */
  const watched = useRef(false);
  /* SAYING IT AT THE TABLE.

     Reported 2026-09-29: "mounting, linking, etc. worked amazing... but it
     didn't broadcast any 'dismounted'." The events reached the activity log,
     which only the DM reads, and a rider being back on foot is the players'
     business.

     Held in a ref and refreshed every render because the announce needs the
     CURRENT link, capabilities and style, and the caller is a 5 s interval whose
     closure was captured when the effect last ran. */
  const say = useRef<(text: string) => void>(() => {});
  const chatOn = layout.tokenLinkEmotes !== false;
  const chatLink = ((layout.roll20Links || {}) as Record<string, { tabId: number; frameId: number; gameId?: string; title?: string }>)[layout.activeCampaign];
  say.current = (text: string) => {
    if (!chatOn || !chatLink || !bridge.capabilities?.roll20ChatAnnounce) return;
    void request<any>({ type: "DM_SCREEN_ROLL20_ANNOUNCE_REQUEST", requestId: newRequestId("lane-chat"), tabId: chatLink.tabId, frameId: chatLink.frameId, gameId: chatLink.gameId, gameTitle: chatLink.title, lines: [{ text, style: String(layout.announceStyle || "desc"), speaker: String(layout.announceSpeaker || "") }] }, { resultType: "DM_SCREEN_ROLL20_ANNOUNCE_RESULT", timeoutMs: 30000 }).catch(() => { /* the activity line already says what happened */ });
  };
  useEffect(() => {
    // Gate on bridge.connected so a connector that dies mid-session stops this
    // 5s poll (P0-1 flips connected→false after ~14s of silence): otherwise each
    // tick stacks a 10s-timeout promise against a dead peer. It restarts on
    // reconnect because connected is in the deps.
    if (!ready || !on || !bridge.connected || !links.length) return;
    let alive = true, busy = false;
    const push = async () => {
      if (busy) return; busy = true;
      try {
        const r = await tokenOp({ op: "follow", links, since: since.current, proneMarker: proneMarkerOf(layout) }, 10000);
        if (!alive || !r.ok || !r.links) return;
        const seq = Number(r.seq) || 0;
        const events = seq < since.current ? [] : (r.events || []);   // a lower seq = the Roll20 tab reloaded
        since.current = seq;
        // Sampled before it is set: the announce below is in this same pass.
        const watchedBefore = watched.current;
        watched.current = true;
        const fresh = r.links;
        if (events.length) {
          update((l) => withTokenLinks(l, applyLaneEvents(tokenLinksOf(l), events)));
          const turn = currentTurnName(layout);
          for (const e of events) { const line = laneEventLine(e, turn); logActivity({ text: line.text, meta: line.meta, tone: e.kind === "thrown" ? "red" : "amber" }); }
          for (const e of laneEventsToAnnounce(events, watchedBefore)) say.current(laneEventLine(e, turn).text);
        } else if (fresh.length) {
          // ids move on when a token is re-dropped; keep the table current by name
          const next = links.map((old) => fresh.find((f) => f.base.name && f.base.name === old.base.name) || old);
          if (JSON.stringify(next) !== key) update((l) => withTokenLinks(l, tokenLinksOf(l).map((old) => { const n = next.find((x) => x.base.name === old.base.name); return n ? keepKinds(tokenLinksOf(l), n) : old; })));
        }
      } finally { busy = false; }
    };
    void push();
    const timer = window.setInterval(() => void push(), 5000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [ready, on, bridge.connected, key, proneMarkerOf(layout)]);
  return null;
}

/** What a row IS, on the map: an ordinary creature, a mount, or a spell's
 *  emanation. One control instead of the base/rider picking, because the
 *  answer is a property of the token and hardly ever changes. */
export function TokenRoleSelect({ name, tokenId }: { name: string; tokenId?: string }) {
  const { layout, update, logActivity } = useStore();
  const role = roleOf(layout, { tokenId, name });
  const def = TOKEN_ROLES.find((r) => r.value === role)!;
  return (
    <select className="xs token-role" value={role} aria-label={`What ${name} is on the map`} title={TOKEN_ROLES.map((r) => `${r.label}: ${r.hint}`).join("\n\n")}
      onChange={(e) => { const next = e.target.value as TokenRole; update((l) => withRole(l, { tokenId, name }, next)); logActivity({ text: `${name}: ${next}`, meta: TOKEN_ROLES.find((r) => r.value === next)?.hint }); }}>
      {TOKEN_ROLES.map((r) => <option key={r.value} value={r.value}>{r.value === def.value ? r.label : r.label}</option>)}
    </select>
  );
}

/** Moving into another creature's space, watched. One map read every few
 *  seconds; when somebody arrives in somebody else's squares the question goes
 *  to the table in Roll20 chat, with three buttons:
 *
 *    onto     climbed on, or mounted. Linked and seated; moves with the base.
 *    under    beneath it, fighting from below. Not linked; the tracker row says
 *             so, and hovering it gives the detail.
 *    through  passing through. Not linked — but ending the turn there is a
 *             squeeze: prone, unless the other creature is two or more sizes
 *             bigger (there is room to stand under a tarrasque).
 *
 *  Even a mount is asked, because a horse can trample somebody and somebody can
 *  duck under a horse — the role decides what "onto" MEANS, not whether the
 *  question is worth asking. The same question sits on the DM Screen too, so
 *  the DM can answer for a monster without going near the tracker.
 *
 *  Only arrivals are asked about, so a pile already standing there when the
 *  sweep first looks is left alone. Mount once, beside TokenFollowKeeper. */
interface RideAsk { key: string; riderId: string; riderName: string; baseId: string; baseName: string; token?: string }
export function RideKeeper() {
  const { layout, update, logActivity } = useStore();
  const ready = useTokenLane();
  const bridge = useBridgeStatus();
  const link = ((layout.roll20Links || {}) as Record<string, { tabId: number; frameId: number; gameId?: string; title?: string }>)[layout.activeCampaign];
  const on = tokenFollowOn(layout) && layout.autoRide !== false && Boolean(link);
  const layoutRef = useRef(layout); layoutRef.current = layout;
  const standing = useRef<Map<string, string>>(new Map());
  const [asks, setAsks] = useState<RideAsk[]>([]);
  const asksRef = useRef<RideAsk[]>([]); asksRef.current = asks;
  const cellPx = useRef(70);

  const say = (text: string) => {
    if (!link || layoutRef.current.auraEmotes === false || !bridge.capabilities?.roll20ChatAnnounce) return;
    void request<any>({ type: "DM_SCREEN_ROLL20_ANNOUNCE_REQUEST", requestId: newRequestId("move-ask"), tabId: link.tabId, frameId: link.frameId, gameId: link.gameId, gameTitle: link.title, lines: [{ text, style: String(layoutRef.current.announceStyle || "desc"), speaker: String(layoutRef.current.announceSpeaker || "") }] }, { resultType: "DM_SCREEN_ROLL20_ANNOUNCE_RESULT", timeoutMs: 15000 }).catch(() => { /* the strip still has the question */ });
  };

  /** Link riders to a base and seat them so every one of them stays visible. */
  const board = async (baseId: string, baseName: string, newcomers: { id: string; name: string }[], isMount: boolean) => {
    const L = layoutRef.current;
    const had = tokenLinksOf(L).find((l) => l.base.id === baseId);
    // A base can already be carrying something that is NOT a rider: the
    // effects engine attaches a caster's emanation to them (Spirit Guardians
    // centred on the cleric). Re-linking has to carry `attached` across, or the
    // spell becomes an ordinary rider that can be dragged off — and it must not
    // be given a seat either, or the ring gets shoved off the caster it belongs to.
    const kept: TokenRef[] = had ? had.riders.filter((x) => !newcomers.some((n) => n.id === x.id)) : [];
    const riders: TokenRef[] = [...kept, ...newcomers];
    const r = await tokenOp({ op: "link", base: baseId, riders: riders.map((x) => ({ id: x.id, name: x.name, ...(x.attached ? { attached: true } : {}) })), centre: false, back: true, group: false, remount: true, mount: isMount, proneMarker: proneMarkerOf(L) });
    if (!r.ok) { logActivity({ text: `${newcomers.map((n) => n.name).join(", ")} could not be linked to ${baseName}`, meta: r.error || "Roll20 refused", tone: "amber" }); return; }
    update((l) => withTokenLinks(l, mergeLink(tokenLinksOf(l), { base: { id: baseId, name: baseName }, riders, remount: true, mount: isMount })));
    const map = await readMap(link as never).catch(() => null);
    if (map?.ok) {
      const base = map.tokens.find((t) => t.id === baseId);
      const seatFor = riders.filter((x) => !x.attached).map((x) => map.tokens.find((t) => t.id === x.id)).filter(Boolean) as RiderToken[];
      if (base && seatFor.length) for (const m of seatMoves(base as RiderToken, seatFor)) await tokenOp({ op: "move", id: m.id, dx: m.dx, dy: m.dy }, 8000);
    }
    logActivity({ text: `${newcomers.map((n) => n.name || "a token").join(", ")} ${newcomers.length === 1 ? (isMount ? "mounts" : "climbs onto") : (isMount ? "mount" : "climb onto")} ${baseName || "it"}`, meta: riders.filter((x) => !x.attached).length > 1 ? `${riders.filter((x) => !x.attached).length} aboard, seated so all are visible` : "moves with it; drag clear to get off" });
  };

  /** One of the three answers, from the chat buttons or from the strip. */
  const answer = async (ask: RideAsk, kind: MoveAnswer, by?: string) => {
    setAsks((list) => list.filter((x) => x.key !== ask.key));
    const L = layoutRef.current;
    const rec: MoveRecord = { kind, riderId: ask.riderId, riderName: ask.riderName, baseId: ask.baseId, baseName: ask.baseName, at: new Date().toISOString(), turnId: String(L.activeTurnId || ""), answeredBy: by };
    update((l) => withMove(l, ask.key, rec));
    if (kind === "onto") {
      const isMount = roleOf(L, { tokenId: ask.baseId, name: ask.baseName }) === "mount";
      await board(ask.baseId, ask.baseName, [{ id: ask.riderId, name: ask.riderName }], isMount);
      return;
    }
    if (kind === "under") {
      logActivity({ text: `${ask.riderName} is underneath ${ask.baseName}`, meta: `not carried${by ? ` · said by ${by}` : ""} — the tracker row shows it until they leave those squares` });
      say(`${ask.riderName} is underneath ${ask.baseName}.`);
      return;
    }
    logActivity({ text: `${ask.riderName} moves through ${ask.baseName}'s space`, meta: `${by ? `said by ${by}; ` : ""}ending the turn there is a squeeze — prone unless ${ask.baseName} is two or more sizes bigger`, tone: "amber" });
  };

  // the chat buttons coming back (connector 0.8.185)
  useEffect(() => onBridge((m: any) => {
    if (m?.type !== "DM_SCREEN_ROLL20_CHAT_COMMAND") return;
    const kind = String(m.command || "");
    if (kind !== "onto" && kind !== "under" && kind !== "through") return;
    const arg = String(m.arg || "");
    const ask = asksRef.current.find((a) => (arg && a.token === arg) || (!arg && asksRef.current.length === 1));
    if (!ask) { logActivity({ text: `${m.by || "Someone"} answered a move question that is no longer open`, meta: arg ? "that question was for an earlier move" : "several are open at once, so the buttons name which", tone: "muted" }); return; }
    void answer(ask, kind as MoveAnswer, String(m.by || ""));
  }), []);

  useEffect(() => {
    // Gate on bridge.connected (see the follow-poll above): a dead connector
    // must not be hammered by this 4s sweep, each tick a stacked timeout promise.
    if (!ready || !on || !bridge.connected) return;
    let alive = true, busy = false, first = true;
    const sweep = async () => {
      if (busy) return; busy = true;
      try {
        const map = await readMap(link as never).catch(() => null);
        if (!alive || !map || !map.ok) return;
        cellPx.current = cellOf(map.page).px;
        const L = layoutRef.current;
        const links = tokenLinksOf(L);
        const tokens: RiderToken[] = map.tokens.map((t) => ({ id: t.id, name: t.name, layer: t.layer, left: t.left, top: t.top, width: t.width, height: t.height }));
        const role = (t: RiderToken) => roleOf(L, { tokenId: t.id, name: t.name });
        const aboardOf = (riderId: string) => links.find((l) => l.riders.some((r) => r.id === riderId && !r.off))?.base.id || null;
        const found = overlaps(tokens, role, (t) => isEffectToken(t));
        const now = new Map<string, string>();
        for (const o of found) now.set(o.rider.id, o.base.id);
        const was = standing.current;
        standing.current = now;
        // a creature that has walked out of those squares is a clean slate again
        for (const [key, rec] of Object.entries(movesOf(L))) if (now.get(rec.riderId) !== rec.baseId) update((l) => withMove(l, key, null));
        const gone = asksRef.current.filter((a) => now.get(a.riderId) !== a.baseId);
        if (gone.length) setAsks((list) => list.filter((a) => !gone.some((g) => g.key === a.key)));
        if (first) { first = false; return; }   // the first look only learns the map
        for (const o of found) {
          const key = pairKey(o.rider.id, o.base.id);
          if (was.get(o.rider.id) === o.base.id) continue;           // was already there: not an arrival
          if (aboardOf(o.rider.id) === o.base.id) continue;           // already riding it
          if (movesOf(L)[key]) continue;                              // already answered
          if (asksRef.current.some((a) => a.key === key)) continue;   // already asked
          const ask: RideAsk = { key, riderId: o.rider.id, riderName: o.rider.name || "a token", baseId: o.base.id, baseName: o.base.name || "it" };
          const isMount = role(o.base) === "mount";
          try {
            const minted = await mintMoveLink(key, `${ask.riderName} → ${ask.baseName}`);
            ask.token = minted.token;
            const style = linkStyleOf(L, Boolean(bridge.capabilities?.roll20ChatCommands));
            const onto = chatAction(isMount ? "mounting" : "climbing on", "onto", minted, style);
            const under = chatAction("going under", "under", minted, style);
            const through = chatAction("moving through", "through", minted, style);
            say(`${ask.riderName} moved into ${ask.baseName}'s space — are you${onto},${under}, or${through}?`);
          } catch { /* the strip still carries the question */ }
          if (!alive) return;
          setAsks((list) => (list.some((x) => x.key === key) ? list : [...list, ask]));
        }
      } finally { busy = false; }
    };
    void sweep();
    const timer = window.setInterval(() => void sweep(), 4000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [ready, on, bridge.connected, link?.tabId, layout.activeCampaign]);

  if (!asks.length) return null;
  return (
    <div className="ride-asks" role="status">
      {asks.map((a) => (
        <div key={a.key} className="apply-hit">
          <span><strong>{a.riderName}</strong> moved into <strong>{a.baseName}</strong>'s space{a.token ? <small className="muted"> · asked in chat</small> : null}</span>
          <button className="small primary" title={`${a.riderName} rides ${a.baseName} and moves with it`} onClick={() => void answer(a, "onto")}>onto</button>
          <button className="small" title={`${a.riderName} is beneath ${a.baseName}, fighting from under it — not carried`} onClick={() => void answer(a, "under")}>under</button>
          <button className="ghost small" title={`Passing through. Ending the turn there is prone unless ${a.baseName} is two or more sizes bigger.`} onClick={() => void answer(a, "through")}>through</button>
        </div>
      ))}
    </div>
  );
}

/** Re-bind a character to a Roll20 token: select the token on the map, click
 *  this. Everything the DM Screen pushes OUT (bars, light, auras, tooltip) and
 *  everything it reads BACK (a bar edited on the token, which is what writes HP
 *  to the real D&D Beyond sheet) travels on this binding, and it is learned by
 *  matching names on Import. A token renamed on the map, a sheet renamed, a
 *  token re-dropped with a new id — any of those break the match silently, and
 *  the symptom is one character whose token edits reach nothing while the
 *  connector carries on reporting their sheet quite happily.
 */
export function TokenBindButton({ characterId, name }: { characterId: string; name: string }) {
  const { layout, update, logActivity } = useStore();
  const bridge = useBridgeStatus();
  const [busy, setBusy] = useState(false);
  const link = ((layout.roll20Links || {}) as Record<string, { tabId: number; frameId: number }>)[layout.activeCampaign];
  const tokens = (layout.roll20Tokens || {}) as Record<string, { tokenId?: string; name?: string }>;
  const bound = tokens[characterId];
  if (!link || !bridge.capabilities?.roll20SelectedInitiative) return null;
  const bind = async () => {
    setBusy(true);
    try {
      const r = await request<{ ok: boolean; selected?: { id: string; name: string }[]; error?: string }>(
        { type: "DM_SCREEN_ROLL20_SELECTION_REQUEST", requestId: newRequestId("bind"), tabId: link.tabId, frameId: link.frameId },
        { resultType: "DM_SCREEN_ROLL20_SELECTION_RESULT", timeoutMs: 15000 });
      const picked = (r.selected || [])[0];
      if (!r.ok || !picked) { logActivity({ text: `${name}: no token selected in Roll20`, meta: r.error || "click their token on the map, then this button", tone: "amber" }); return; }
      update((l) => ({ ...l, roll20Tokens: { ...((l.roll20Tokens as Record<string, unknown>) || {}), [characterId]: { tokenId: String(picked.id), name: String(picked.name || "") } } }));
      logActivity({ text: `${name} is bound to "${picked.name || "the selected token"}"`, meta: "bar edits on that token now reach their sheet, and the sheet reaches the token", tone: "green" });
    } catch (e) { logActivity({ text: `${name}: could not read the Roll20 selection`, meta: e instanceof Error ? e.message : String(e), tone: "red" }); }
    finally { setBusy(false); }
  };
  return (
    <button className={`ghost small ${bound?.tokenId ? "on" : ""}`} disabled={busy} onClick={() => void bind()}
      aria-label={`Bind ${name} to the selected Roll20 token`}
      title={bound?.tokenId ? `Bound to "${bound.name || bound.tokenId}". Select a different token on the map and click to re-bind.` : `${name} is NOT bound to a token. Bar edits on the map will not reach their sheet. Select their token in Roll20 and click.`}>
      {busy ? "…" : "⇄"}
    </button>
  );
}

/** Self-heals a STALE Roll20 link. The DM Screen stores the Roll20 game's TAB id
 *  (roll20Links[campaign].tabId) and sends every token / bar / turn op to it. When
 *  that tab is closed or reloaded it gets a NEW id; nothing refreshed the stored
 *  one, so the keepers above fired into a tab that no longer exists and the health
 *  board read "Roll20: No tab with id …". That stranded the bar pushes mid-session
 *  — a re-dropped token never got its HP bar or its show-to-players flag. This
 *  watches for exactly that error and re-discovers the live tab for the SAME game,
 *  then updates the link so the next op lands. Mount once, beside TokenFollowKeeper. */
export function Roll20Rebinder() {
  const { layout, update, logActivity } = useStore();
  const health = useHealth();
  const links = (layout.roll20Links || {}) as Record<string, { tabId: number; frameId: number; gameId?: string }>;
  const link = links[layout.activeCampaign];
  const r20 = health.roll20;
  // The ONE fault a rebind can fix: a dead stored tab. Any other Roll20 failure is
  // left for the health board to show — re-pointing the tab would not help it.
  const staleTab = r20.status === "down" && /no tab with id/i.test(r20.error || "");
  const busy = useRef(false);
  const lastTry = useRef(0);
  useEffect(() => {
    if (!staleTab || !link || !link.gameId || busy.current || Date.now() - lastTry.current < 5000) return;
    busy.current = true; lastTry.current = Date.now();
    let alive = true;
    void (async () => {
      try {
        // Discovery carries no tabId, so it cannot fail on the dead one: it returns
        // every open Roll20 game tab, and we pick the one for our game.
        const r = await request<{ ok?: boolean; pages?: { gameId?: string; tabId?: number; frameId?: number }[] }>(
          { type: "DM_SCREEN_ROLL20_REQUEST", requestId: newRequestId("rebind"), allowEmpty: true },
          { resultType: "DM_SCREEN_ROLL20_RESULT", timeoutMs: 15000 });
        const page = (r.pages || []).find((p) => p && p.gameId === link.gameId && p.tabId);
        if (!alive || !page || page.tabId === link.tabId) return;
        update((l) => {
          const all = { ...((l.roll20Links as Record<string, { tabId: number; frameId: number; gameId?: string }>) || {}) };
          const cur = all[l.activeCampaign];
          if (!cur || cur.gameId !== link.gameId) return l;   // the active campaign or its link moved on while we asked
          all[l.activeCampaign] = { ...cur, tabId: page.tabId!, frameId: page.frameId ?? cur.frameId };
          return { ...l, roll20Links: all };
        }, { noUndo: true });
        logActivity({ text: "Roll20 re-linked to the current game tab", meta: `the tab had changed (was ${link.tabId}); now ${page.tabId} — token and bar sync will land again`, tone: "green" });
      } catch { /* the next failure re-triggers this */ }
      finally { busy.current = false; }
    })();
    return () => { alive = false; };
  }, [staleTab, r20.errorAt, link?.gameId, link?.tabId, layout.activeCampaign]);
  return null;
}